import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import i18n from 'i18n-js';
import moment from 'moment';
import { getEventDescription } from 'utils';
import { Card } from 'components/UI';
import useStyles from './Logs.styled';
import Box from '@material-ui/core/Box';
import Loading from 'components/Loading';
import Paginator from 'components/Paginator';
import { logsActions } from 'state/modules/logs';
import Typography from '@material-ui/core/Typography';
import { FETCH_STATE } from 'constants';
import { debounce } from 'lodash';

const Logs = ({ title, targetType, targetId, secondary, refresh, organizationId, shared }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    include_secondary: secondary ? true : undefined,
    target_type: targetType,
    shared: shared ? true : undefined,
    target_id: targetId ? targetId : undefined,
    organization_id: organizationId ? organizationId : undefined,
    page: 1,
    limit: 10,
  });

  const logs = useSelector((state) => state.logs.logs);
  const fetchingLogs = useSelector((state) => state.logs.fetchingLogs);
  const pagination = useSelector((state) => state.logs.pagination);

  const loading = fetchingLogs === FETCH_STATE.LOADING;

  const { localeFormat } = useSelector((state) => state.organizations);

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page
   */
  const handleChangePage = (_, newPage) => {
    setParams((params) => ({ ...params, page: newPage + 1 }));
  };

  /**
   * Handle limit change
   * @param {object} event With the value of the new limit
   */
  const handleChangeRowsPerPage = (event) => {
    const limit = event.target.value;
    setParams((params) => ({
      ...params,
      page: 1,
      limit,
    }));
  };

  /**
   * Get logs according to type
   * @param {object} params Current params to fetch logs
   */
  const fetchLogs = useCallback(
    debounce((params) => {
      const logsParams = {
        ...params,
      };
      dispatch(logsActions.fetchLogs(logsParams));
    }, 1000),
    [dispatch]
  );

  useEffect(() => {
    fetchLogs(params);
  }, [params, fetchLogs]);

  useEffect(() => {
    setParams((params) => ({
      ...params,
      include_secondary: secondary ? true : undefined,
      target_type: targetType,
      shared: shared ? true : undefined,
      target_id: targetId ? targetId : undefined,
      organization_id: organizationId ? organizationId : undefined,
    }));
  }, [secondary, targetType, shared, targetId, organizationId]);

  useEffect(() => {
    if (refresh) {
      fetchLogs(params);
    }
  }, [refresh]); // eslint-disable-line

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: 'Refresh',
      onClick: () => {
        const newParams = { ...params, page: 1 };
        setParams(newParams);
      },
    },
  ];

  /**
   * Render item of the log
   * @param {object} item Current item to be rendered
   * @param {boolea} last Check if item is the last element of array
   */
  const LogItem = ({ item, last }) => {
    const isToday = moment(item.timestamp).isSame(moment(), 'day');

    return (
      <Box display="flex" position="relative">
        <Box className={clsx(classes.timeline, { [classes.lastItem]: last })}>
          <Box className={clsx(classes.circle, { [classes.primary]: isToday })} />
          {!last && <Box className={classes.line} />}
        </Box>
        <Box pl={3} mb={2} display="flex" flexDirection="column">
          <Typography style={{ lineHeight: 1 }}>
            <Box
              fontSize={13}
              component="span"
              fontWeight="medium"
              className={classes.textSecondary}
            >
              {getEventDescription(item)}
            </Box>
          </Typography>
          <Typography color="textSecondary" style={{ lineHeight: 1 }}>
            <Box fontSize={12} component="span">
              {moment(item.timestamp).format(`${localeFormat} hh:mm a`)}
            </Box>
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Card title={title} menuItems={items} headerActionType="vertical" datacy="logs-widget">
      <Box className={classes.root}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <Loading />
          </Box>
        ) : (
          <>
            {logs?.length > 0 ? (
              <Box
                style={{
                  height: '100%',
                  maxHeight: 'calc(100% - 52px)',
                  minHeight: 150,
                  overflowY: 'auto',
                }}
              >
                {logs.map((val, index) => {
                  return <LogItem item={val} key={index} last={logs.length === index + 1} />;
                })}
              </Box>
            ) : (
              <Typography variant="h4">
                <Box fontSize={14}>{i18n.t('common.empty_list')}</Box>
              </Typography>
            )}
            <Box mt={2} display="flex" alignItems="center" justifyContent="flex-end">
              <Paginator
                component="div"
                limit={params.limit}
                onPageChange={handleChangePage}
                labelRowsPerPage="Page size"
                labelDisplayedRows={(data) => `${data.from}-${data.to}`}
                pagination={pagination}
                unknown_count={true}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

Logs.propTypes = {
  height: PropTypes.number,
  secondary: PropTypes.bool,
  refresh: PropTypes.bool,
  title: PropTypes.string.isRequired,
  targetId: PropTypes.number,
  targetType: PropTypes.string,
};

Logs.defaultProps = {
  title: 'Event Log',
  secondary: true,
  refresh: false,
  height: 300,
};

export default Logs;
